import React, { useState } from "react";
import { useDebounce } from "use-debounce";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Empty,
  Drawer,
  Spin,
  Dropdown,
  Modal,
  Tag,
  Space,
  Typography,
  Divider,
} from "antd";
import {
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  SearchOutlined,
  DollarOutlined,
  PlusOutlined,
  DeleteOutlined as DeleteIcon,
  ShoppingCartOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { useOnlineOrderService, useProductsQuery } from "../../Services";
import { PAGE_SIZE, DEBOUNCED_SEARCH_QUERY, ROUTE } from "../../Constant";
import {
  PageHeader,
  tablePagination,
  DRAWER_FORM_WIDTH,
  drawerFooter,
  drawerTitle,
} from "../../components";
import { useCart } from "../../Context";
import { useSectionAccess } from "../../Services";
import { IntegrationAction } from "../../Types";
import { CustomerModal } from "../../components";

const { Option } = Select;

type OrderStatus = "pending" | "approved" | "completed" | "cancelled";
type OrderType = "whatsapp" | "online";

interface OrderItem {
  product_id: string;
  stock_id: string;
  qty: number;
  product_name?: string;
  batch_no?: string;
  price?: number;
}

interface OnlineOrder {
  id: string;
  type: OrderType;
  customer_phone: string;
  customer_name: string;
  order_details: string;
  items?: OrderItem[];
  status: OrderStatus;
  created_at: string;
  updated_at: string;
}

const STATUS_COLOR: Record<OrderStatus, string> = {
  pending: "gold",
  approved: "blue",
  completed: "green",
  cancelled: "red",
};

const STATUS_LABEL: Record<OrderStatus, string> = {
  pending: "Pending",
  approved: "Approved",
  completed: "Completed",
  cancelled: "Cancelled",
};

const TYPE_COLOR: Record<OrderType, string> = {
  whatsapp: "green",
  online: "blue",
};

export const OnlineOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { setCart, setOnlineOrderId } = useCart();
  const { isAllowed } = useSectionAccess("integrations");

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined
  );
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<OnlineOrder | null>(null);
  const [editingItems, setEditingItems] = useState<OrderItem[]>([]);
  const [form] = Form.useForm();
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [customerFormData, setCustomerFormData] = useState<{
    name?: string;
    phone?: string;
    address?: string;
  }>({});

  // Product picker state inside drawer
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [debouncedProductSearch] = useDebounce(productSearchQuery, 400);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [selectedStockId, setSelectedStockId] = useState<string | null>(null);
  const [itemQty, setItemQty] = useState<number>(1);

  const { onlineOrdersQuery, updateOnlineOrderMutation, deleteOnlineOrderMutation } =
    useOnlineOrderService({
      search,
      status: statusFilter,
      type: typeFilter,
      page,
      limit: PAGE_SIZE,
    });

  const { data: orderResponse, isLoading, refetch } = onlineOrdersQuery;
  const orders: OnlineOrder[] = orderResponse?.data || [];
  const total = orderResponse?.total || 0;

  // Product search for the item picker
  const { data: productsData } = useProductsQuery(
    { search: debouncedProductSearch, page: 1, limit: 20 },
    { enabled: debouncedProductSearch.length > 0 }
  );
  const availableProducts = productsData?.data || [];

  const openDrawer = (order: OnlineOrder) => {
    setEditingOrder(order);
    setEditingItems(order.items || []);
    setSelectedProduct(null);
    setSelectedStockId(null);
    setItemQty(1);
    setProductSearchQuery("");
    form.setFieldsValue({
      customer_name: order.customer_name,
      customer_phone: order.customer_phone,
      order_details: order.order_details,
      status: order.status,
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingOrder(null);
    setEditingItems([]);
    setSelectedProduct(null);
    setSelectedStockId(null);
    setItemQty(1);
    setProductSearchQuery("");
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!editingOrder) return;

      const res = await updateOnlineOrderMutation.mutateAsync({
        id: editingOrder.id,
        type: editingOrder.type,
        ...values,
        items: editingItems,
      });

      if (res?.success) {
        message.success("Order updated successfully!");
        refetch();
        closeDrawer();
      } else {
        message.error(res?.message || "Update failed.");
      }
    } catch {
      message.error("Please fill all required fields.");
    }
  };

  const handleDelete = (order: OnlineOrder) => {
    Modal.confirm({
      title: "Delete Order",
      content: `Are you sure you want to delete the order for "${order.customer_name || order.customer_phone}"?`,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        const res = await deleteOnlineOrderMutation.mutateAsync({ id: order.id });
        if (res?.success) {
          message.success("Order deleted successfully!");
          if (orders.length === 1 && page > 1) setPage(page - 1);
        } else {
          message.error(res?.message || "Failed to delete order.");
        }
      },
    });
  };

  const handleProductSelect = (productId: string) => {
    const product = availableProducts.find((p: any) => p.id === productId);
    setSelectedProduct(product || null);
    setSelectedStockId(null);
    setItemQty(1);
  };

  const handleAddItem = () => {
    if (!selectedProduct || !selectedStockId) {
      message.warning("Please select a product and stock batch.");
      return;
    }
    const stock = selectedProduct.stocks?.find((s: any) => String(s.id) === selectedStockId);
    if (!stock) return;

    setEditingItems((prev) => [
      ...prev,
      {
        product_id: selectedProduct.id,
        stock_id: selectedStockId,
        qty: itemQty,
        product_name: selectedProduct.title,
        batch_no: stock.batch_no,
        price: stock.retail_price,
      },
    ]);
    setSelectedProduct(null);
    setSelectedStockId(null);
    setItemQty(1);
    setProductSearchQuery("");
  };

  const handleRemoveItem = (index: number) => {
    setEditingItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleProceedToPay = (order: OnlineOrder) => {
    const items = order.items || [];
    if (items.length === 0) {
      message.warning("This order has no items mapped. Edit the order first.");
      return;
    }

    const cartItems = items.map((item) => ({
      id: item.product_id as any,
      name: item.product_name || `Product #${item.product_id}`,
      price: item.price || 0,
      image: "",
      batch_no: item.batch_no || "",
      qty: item.qty,
      rawProduct: {
        id: item.product_id as any,
        title: item.product_name || "",
        unit: "",
        stocks: [
          {
            id: item.stock_id as any,
            batch_no: item.batch_no || "",
            retail_price: item.price || 0,
            wholesale_price: 0,
            cost_price: 0,
            quantity: item.qty,
          },
        ],
      },
    }));

    setCart(cartItems);
    setOnlineOrderId(order.id);
    message.success(`Added ${cartItems.length} item(s) to cart.`);
    navigate(`/system/${ROUTE.sales_billing}`);
  };

  const columns = [
    {
      title: "Customer",
      key: "customer",
      render: (_: any, rec: OnlineOrder) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
            <span className="text-gray-600 font-semibold text-xs">
              {(rec.customer_name || rec.customer_phone)?.[0]?.toUpperCase() ??
                "?"}
            </span>
          </div>
          <div>
            <p className="font-medium text-gray-800 text-sm m-0 leading-tight">
              {rec.customer_name || <span className="text-gray-400">—</span>}
            </p>
            <p className="text-gray-400 text-xs m-0 leading-tight">
              {rec.customer_phone}
            </p>
          </div>
        </div>
      ),
    },
    {
      title: "Source",
      key: "type",
      width: 110,
      render: (_: any, rec: OnlineOrder) => (
        <Tag color={TYPE_COLOR[rec.type]} className="text-xs capitalize">
          {rec.type}
        </Tag>
      ),
    },
    {
      title: "Order Details",
      dataIndex: "order_details",
      key: "order_details",
      render: (v: string) => (
        <span className="text-sm text-gray-700 whitespace-pre-line">{v}</span>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: (_: any, rec: OnlineOrder) => (
        <Tag color={STATUS_COLOR[rec.status]} className="text-xs">
          {STATUS_LABEL[rec.status]}
        </Tag>
      ),
    },
    {
      title: "Date",
      key: "created_at",
      width: 140,
      render: (_: any, rec: OnlineOrder) => (
        <span className="text-xs text-gray-500">
          {new Date(rec.created_at).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center" as const,
      render: (_: any, rec: OnlineOrder) => {
        const hasItems = (rec.items || []).length > 0;
        const menuItems = [
          ...(hasItems && rec.status !== "completed" && isAllowed(IntegrationAction.OnlineOrders)
            ? [
                {
                  key: "pay",
                  label: "Proceed to Pay",
                  icon: <DollarOutlined />,
                  onClick: () => handleProceedToPay(rec),
                },
                { type: "divider" as const },
              ]
            : []),
          ...(isAllowed(IntegrationAction.OnlineOrders)
            ? [
                {
                  key: "saveCustomer",
                  label: "Save as Customer",
                  icon: <UserAddOutlined />,
                  onClick: () => {
                    setCustomerFormData({
                      name: rec.customer_name,
                      phone: rec.customer_phone,
                    });
                    setCustomerModalOpen(true);
                  },
                },
              ]
            : []),
          ...(isAllowed(IntegrationAction.OnlineOrders)
            ? [
                {
                  key: "edit",
                  label: "Edit",
                  icon: <EditOutlined />,
                  onClick: () => openDrawer(rec),
                },
              ]
            : []),
          ...(isAllowed(IntegrationAction.OnlineOrders)
            ? [
                {
                  key: "delete",
                  label: "Delete",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => handleDelete(rec),
                },
              ]
            : []),
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              size="small"
              className="text-gray-400"
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="Online Orders"
        subText={`Total: ${total} order${total !== 1 ? "s" : ""}`}
        handleButtons={[
          {
            title: "Refresh",
            icon: ReloadOutlined,
            onClick: () => refetch(),
          },
        ]}
        searchBar={
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search by name or phone…"
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(1);
              }}
              allowClear
              style={{ width: 240 }}
            />
            <Select
              placeholder="Status"
              allowClear
              style={{ width: 130 }}
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                setPage(1);
              }}
            >
              <Option value="pending">Pending</Option>
              <Option value="approved">Approved</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
            <Select
              placeholder="Source"
              allowClear
              style={{ width: 120 }}
              value={typeFilter}
              onChange={(v) => {
                setTypeFilter(v);
                setPage(1);
              }}
            >
              <Option value="whatsapp">WhatsApp</Option>
              <Option value="online">Online</Option>
            </Select>
          </div>
        }
      />

      <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden">
        <Table
          dataSource={orders}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={tablePagination({
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: setPage,
            itemName: "orders",
          })}
          scroll={{ x: 800 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No orders found"
              />
            ),
          }}
        />
      </div>

      <Drawer
        title={drawerTitle("Edit Order")}
        open={drawerOpen}
        onClose={closeDrawer}
        width={DRAWER_FORM_WIDTH}
        footer={drawerFooter({
          actions: [
            { label: "Cancel", onClick: closeDrawer },
            ...(isAllowed(IntegrationAction.OnlineOrders)
              ? [
                  {
                    label: "Save Changes" as const,
                    type: "primary" as const,
                    loading: updateOnlineOrderMutation.isPending,
                    onClick: handleSubmit,
                  },
                ]
              : []),
          ],
        })}
      >
        <Spin spinning={updateOnlineOrderMutation.isPending}>
          <Form form={form} layout="vertical">
            <Form.Item label="Customer Name" name="customer_name">
              <Input placeholder="Enter customer name" />
            </Form.Item>

            <Form.Item label="Contact Number" name="customer_phone">
              <Input placeholder="Enter phone number" />
            </Form.Item>

            <Form.Item
              label="Order Details"
              name="order_details"
              rules={[
                { required: true, message: "Please enter order details." },
              ]}
            >
              <Input.TextArea
                placeholder="Describe the order items"
                rows={4}
                style={{ resize: "none" }}
              />
            </Form.Item>

            <Form.Item
              label="Status"
              name="status"
              rules={[{ required: true, message: "Please select a status." }]}
            >
              <Select placeholder="Select status">
                <Option value="pending">Pending</Option>
                <Option value="approved">Approved</Option>
                <Option value="completed">Completed</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </Form.Item>
          </Form>

          <Divider />
          <Typography.Text strong className="text-base">
            <ShoppingCartOutlined className="mr-1" />
            Order Items
          </Typography.Text>
          <div className="mt-3">
            <Space.Compact block>
              <Select
                showSearch
                placeholder="Search product..."
                style={{ width: 240 }}
                value={selectedProduct?.id || undefined}
                onSearch={setProductSearchQuery}
                onChange={handleProductSelect}
                filterOption={false}
                notFoundContent={
                  debouncedProductSearch ? "No products found" : "Type to search"
                }
                allowClear
                onClear={() => {
                  setSelectedProduct(null);
                  setSelectedStockId(null);
                }}
              >
                {availableProducts.map((p: any) => (
                  <Option key={p.id} value={p.id}>
                    {p.title} ({p.sku_code})
                  </Option>
                ))}
              </Select>
              <Select
                placeholder="Batch"
                style={{ width: 130 }}
                value={selectedStockId}
                onChange={setSelectedStockId}
                disabled={!selectedProduct}
                allowClear
                onClear={() => setSelectedStockId(null)}
              >
                {(selectedProduct?.stocks || []).map((s: any) => (
                  <Option key={s.id} value={s.id}>
                    {s.batch_no} (qty: {s.quantity})
                  </Option>
                ))}
              </Select>
              <InputNumber
                min={1}
                value={itemQty}
                onChange={(v) => setItemQty(v || 1)}
                style={{ width: 70 }}
                disabled={!selectedStockId}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={!selectedStockId}
                onClick={handleAddItem}
              >
                Add
              </Button>
            </Space.Compact>

            {editingItems.length > 0 && (
              <div className="mt-3 border rounded-md divide-y">
                {editingItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between px-3 py-2 text-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="font-medium">
                        {item.product_name || `Product #${item.product_id}`}
                      </span>
                      <span className="text-gray-400 mx-1">×</span>
                      <span className="font-semibold">{item.qty}</span>
                      {item.batch_no && (
                        <span className="text-gray-400 ml-2 text-xs">
                          [{item.batch_no}]
                        </span>
                      )}
                    </div>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteIcon />}
                      onClick={() => handleRemoveItem(idx)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </Spin>
      </Drawer>

      <CustomerModal
        open={customerModalOpen}
        onCancel={() => setCustomerModalOpen(false)}
        onSuccess={() => {
          setCustomerModalOpen(false);
        }}
        initialValues={customerFormData}
      />
    </div>
  );
};
